
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

export const CredentialValidationStatus = () => {
  const [validationStatus, setValidationStatus] = useState<{
    isLoading: boolean;
    hasCredentials: boolean;
    error: string | null;
    userEmail: string | null;
  }>({
    isLoading: true,
    hasCredentials: false,
    error: null,
    userEmail: null,
  });

  useEffect(() => {
    const validateCredentials = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError || !user) {
          setValidationStatus({
            isLoading: false,
            hasCredentials: false,
            error: 'Not authenticated',
            userEmail: null,
          });
          return;
        }

        // Check for Lutra credentials
        const { data: credentials, error: credError } = await supabase
          .from('platform_credentials')
          .select('*')
          .eq('user_id', user.id)
          .eq('platform', 'lutra-webhook')
          .eq('is_active', true)
          .maybeSingle();

        if (credError) {
          console.error('Credential check error:', credError);
          setValidationStatus({
            isLoading: false,
            hasCredentials: false,
            error: `Database error: ${credError.message}`,
            userEmail: user.email,
          });
          return;
        }

        // Type-safe credential validation
        const hasValidCredentials = credentials && 
          credentials.credentials && 
          typeof credentials.credentials === 'object' &&
          credentials.credentials !== null &&
          !Array.isArray(credentials.credentials) &&
          'webhookUrl' in credentials.credentials && 
          'webhookToken' in credentials.credentials &&
          credentials.credentials.webhookUrl &&
          credentials.credentials.webhookToken;

        setValidationStatus({
          isLoading: false,
          hasCredentials: !!hasValidCredentials,
          error: null,
          userEmail: user.email,
        });

      } catch (e: any) {
        console.error('Validation error:', e);
        setValidationStatus({
          isLoading: false,
          hasCredentials: false,
          error: `Validation failed: ${e.message}`,
          userEmail: null,
        });
      }
    };

    validateCredentials();
  }, []);

  if (validationStatus.isLoading) {
    return (
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Validating Credentials...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          {validationStatus.hasCredentials ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : validationStatus.error ? (
            <XCircle className="h-4 w-4 text-red-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          )}
          Job Search Credentials Status
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">User:</span>
            <span className="text-sm font-mono">{validationStatus.userEmail || 'Unknown'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Lutra Credentials:</span>
            <Badge variant={validationStatus.hasCredentials ? 'default' : 'destructive'}>
              {validationStatus.hasCredentials ? 'Configured' : 'Missing'}
            </Badge>
          </div>
          {validationStatus.error && (
            <div className="text-sm text-red-600 mt-2">
              Error: {validationStatus.error}
            </div>
          )}
          {!validationStatus.hasCredentials && !validationStatus.error && (
            <div className="text-sm text-yellow-600 mt-2">
              Job search functionality requires Lutra webhook credentials to be configured.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
